import without from 'lodash/without';
import Logger from 'observability/Logger';
import { getFormNode } from 'common/entities/formNode/selectors';

import { ENTITIES_KEY_NODE } from 'common/entities/formNode';

import {
    NODE_NAME_ADDITIONAL_GUEST_FIRST_NAME,
    NODE_NAME_ADDITIONAL_GUEST_LAST_NAME,
    NODE_NAME_ADDITIONAL_GUEST_EMAIL,
    NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP,
    NODE_NAME_SALE_INFORMATION,
} from 'common/entities/formNode/nodeNames';
import {
    NODE_TYPE_ROOT,
    NODE_TYPE_GROUP,
    NODE_TYPE_QUESTION,
    NODE_TYPE_CONTENT,
} from 'common/entities/formNode/nodeTypes';
import { BEGIN_DRAG_FORM_NODE, HOVER_DRAG_FORM_NODE, END_DRAG_FORM_NODE } from '../constants/actionTypes';
import {
    getFormId,
    getDragNodeId,
    getDropNodeId,
    getDropParentNodeId,
    getDropNodePosition,
    isAdditionalGuestInfoNode,
    getBlockedNodes,
    getAdditionalGuestInfoNodes,
} from '../selectors';

const LOG = new Logger('dragActions');

/**
 * @param  {Function} fn - dangerous function
 * @return {Function} - safe function. Thrown errors will be transformed into logs.
 */
const tryWrap =
    (fn) =>
    (...args) => {
        try {
            return fn(...args);
        } catch (err) {
            LOG.warn('Error on tryWrap', err);
            return undefined;
        }
    };

const createActionBeginDragFormNode = (dragNodeId, dragNodePosition) => ({
    type: BEGIN_DRAG_FORM_NODE,
    dragNodeId,
    dragNodePosition,
});

const createActionHoverDropFormNode = (dropNodeId, dropParentNodeId, dropNodePosition) => ({
    type: HOVER_DRAG_FORM_NODE,
    dropNodeId,
    dropParentNodeId,
    dropNodePosition,
});

/**
 * @param {Object} nodeEntities
 * @param {string} nodeId
 * @param {function|string} predicate - function to evaluate or node id.
 */
const isNodeInSubtree = (nodeEntities, nodeId, predicate) => {
    const node = nodeEntities[nodeId];
    if (node == null) {
        return false;
    }
    if (typeof predicate === 'string' ? nodeId === predicate : predicate(nodeId)) {
        return true;
    }
    return isNodeInSubtree(nodeEntities, node.parent, predicate);
};

/**
 * @param {Object} nodeEntities
 * @param {string} nodeId
 * @param {string} parentNodeName
 */
const isNodeInSubtreeNodeName = (nodeEntities, nodeId, parentNodeName) =>
    isNodeInSubtree(nodeEntities, nodeId, (parentId) => nodeEntities[parentId].name === parentNodeName);

/**
 * Returns the top node of the tree containing the given node.
 * @param {string} nodeId
 * @param {Object} nodeEntities
 * @return {string}
 */
const getSubtreeTopNodeId = (nodeId, nodeEntities) => {
    const node = nodeEntities[nodeId];
    return node.parent == null ? nodeId : getSubtreeTopNodeId(node.parent, nodeEntities);
};

/** @var {Object} - functions that evaluate if node can be dropped by drop parent node name */
const canDropNodeByDropParentNodeName = {
    [NODE_NAME_SALE_INFORMATION]: () => false,
};

/** @var {Object} - functions that evaluate if node can be dropped by drag node name */
const canDropNodeByDragNodeName = {
    [NODE_NAME_ADDITIONAL_GUEST_FIRST_NAME]: ({ dropParentNodeId, nodeEntities }) =>
        isNodeInSubtreeNodeName(nodeEntities, dropParentNodeId, NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP),
    [NODE_NAME_ADDITIONAL_GUEST_LAST_NAME]: ({ dropParentNodeId, nodeEntities }) =>
        isNodeInSubtreeNodeName(nodeEntities, dropParentNodeId, NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP),
    [NODE_NAME_ADDITIONAL_GUEST_EMAIL]: ({ dropParentNodeId, nodeEntities }) =>
        isNodeInSubtreeNodeName(nodeEntities, dropParentNodeId, NODE_NAME_ADDITIONAL_GUEST_INFO_GROUP),
};

/**
 * Returns true if drag node can be dropped at the indicated position.
 * @param {string} dragNodeId
 * @param {string} dropNodeId
 * @param {string} dropParentNodeId
 * @param {number} position
 * @param {Object} nodeEntities
 */
export const canDropNode = (dragNodeId, dropParentNodeId, position, nodeEntities) => {
    const dropParentNode = nodeEntities[dropParentNodeId];
    const dragNode = nodeEntities[dragNodeId];

    const args = {
        dropParentNodeId,
        nodeEntities,
        position,
    };

    const limitationByParentNodeName = canDropNodeByDropParentNodeName[dropParentNode.name];
    if (limitationByParentNodeName && !limitationByParentNodeName(args)) {
        return false;
    }

    const limitationDragNodeName = canDropNodeByDragNodeName[dragNode.name];
    if (limitationDragNodeName && !limitationDragNodeName(args)) {
        return false;
    }

    return true;
};

// /////////////////////////////////////////////////////////////////////////////////////////////////
// Thunks
// ⬇️⬇️⬇️⬇️⬇️⬇️
// /////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Start form node drag-and-drop
 * @param  {string} dragNodeId - drag source node ID
 */
export const beginDragFormNode = (dragNodeId) => (dispatch, getState) =>
    tryWrap(() => {
        const state = getState();
        const dragNode = getFormNode(state, dragNodeId);
        const dragNodeParentId = dragNode.parent;
        const dragNodeParent = getFormNode(state, dragNodeParentId);
        const dragNodePosition = dragNodeParent.children.indexOf(dragNodeId);

        dispatch(createActionBeginDragFormNode(dragNodeId, dragNodePosition));
    })();

/**
 * Updates the drag-and-drop information
 * @param  {Object} dropNodeId - drop target node ID
 * @param  {number} [yPosition] - y-position of the hover as a ratio (0 is the top, 1 is the bottom)
 */
export const hoverDropFormNode = (dropNodeId, yPosition) => (dispatch, getState) =>
    tryWrap(() => {
        const state = getState();
        const nodeEntities = state.entities[ENTITIES_KEY_NODE];

        const dragNodeId = getDragNodeId(state);
        const dragNode = getFormNode(state, dragNodeId);
        const dragNodeParentId = dragNode.parent;
        const dragNodeParent = getFormNode(state, dragNodeParentId);
        const dragNodeSiblingPosition = dragNodeParent.children.indexOf(dragNodeId);

        const dropNode = getFormNode(state, dropNodeId);
        const dropNodeParentId = dropNode.parent;

        // If no drop parent, dropNode could be root. Happens when dragging outside. Skip case.
        // Skip when dropNode and dragNode aren't in the same group
        if (!dropNodeParentId || dropNode.parent !== dragNode.parent) {
            return;
        }

        const dropNodeParent = getFormNode(state, dropNodeParentId);
        const dropNodeSiblingPosition = dropNodeParent.children.indexOf(dropNodeId);
        const dropNodeType = dropNode.type;

        // Dragging a node down within the same parent node is a special case
        const dropAfterDragNode =
            dragNodeParentId === dropNodeParentId && dropNodeSiblingPosition > dragNodeSiblingPosition;

        const movement = {
            dropParentNodeId: null,
            dropNodePosition: null,
        };

        switch (true) {
            // Drop target is the same as or a child of the drag source
            case dragNodeId === dropNodeId:
            case isNodeInSubtree(nodeEntities, dropNodeId, dragNodeId): {
                movement.dropParentNodeId = dragNodeParentId;
                movement.dropNodePosition = dragNodeSiblingPosition;
                break;
            }
            // Prepend to the drop target
            case dropNodeType === NODE_TYPE_GROUP && yPosition > 0.5 && yPosition <= 1:
            case dropNodeType === NODE_TYPE_ROOT && yPosition <= 1: {
                movement.dropParentNodeId = dropNodeId;
                movement.dropNodePosition = 0;
                break;
            }
            // Append to the drop target
            case dropNodeType === NODE_TYPE_ROOT: {
                movement.dropParentNodeId = dropNodeId;
                movement.dropNodePosition = dropNode.children.length;
                break;
            }
            // Insert before the drop target
            case dropNodeType === NODE_TYPE_GROUP && yPosition <= 1:
            case dropNodeType === NODE_TYPE_CONTENT && yPosition <= 0.5:
            case dropNodeType === NODE_TYPE_QUESTION && yPosition <= 0.5: {
                movement.dropParentNodeId = dropNodeParentId;
                movement.dropNodePosition = dropAfterDragNode ? dropNodeSiblingPosition - 1 : dropNodeSiblingPosition;
                break;
            }
            // Insert after the drop target
            case dropNodeType === NODE_TYPE_GROUP && yPosition > 1:
            case dropNodeType === NODE_TYPE_CONTENT:
            case dropNodeType === NODE_TYPE_QUESTION: {
                movement.dropParentNodeId = dropNodeParentId;
                movement.dropNodePosition = dropAfterDragNode ? dropNodeSiblingPosition : dropNodeSiblingPosition + 1;
                break;
            }
            // If all else fails, append to the root
            default: {
                const rootNodeId = getSubtreeTopNodeId(dropNodeId, nodeEntities);
                const rootNode = getFormNode(state, rootNodeId);
                movement.dropParentNodeId = null;
                movement.dropNodePosition = rootNode ? rootNode.children.length : 0;
                break;
            }
        }

        const targetPositionChanged =
            dropNodeId !== getDropNodeId(state) ||
            movement.dropParentNodeId !== getDropParentNodeId(state) ||
            movement.dropNodePosition !== getDropNodePosition(state);

        if (
            targetPositionChanged &&
            canDropNode(dragNodeId, movement.dropParentNodeId, movement.dropNodePosition, nodeEntities)
        ) {
            dispatch(createActionHoverDropFormNode(dropNodeId, movement.dropParentNodeId, movement.dropNodePosition));
        }
    })();

const createEndDragFormNodeAction = (
    dragNodeId,
    dropParentNodeId,
    previousParent,
    dropNodePosition,
    dragNode,
    blockedNodes,
) => {
    const previousParentUpdated = {
        ...previousParent,
        children: previousParent.children.filter((nodeId) => nodeId !== dragNodeId),
    };

    const newParentUpdatedChildren = without(
        [
            ...(previousParentUpdated.children || []).slice(0, dropNodePosition),
            dragNodeId,
            ...(previousParentUpdated.children || []).slice(dropNodePosition),
        ],
        ...blockedNodes,
    );

    const parentNodeChildren = previousParent.children || [];

    const groupBlockedNodes = blockedNodes.filter((id) => parentNodeChildren.includes(id)).length ? blockedNodes : [];

    const newParentUpdated = {
        ...previousParentUpdated,
        children: [...groupBlockedNodes, ...newParentUpdatedChildren],
    };

    const newDragNode = {
        ...dragNode,
        parent: newParentUpdated.id,
    };

    return {
        type: END_DRAG_FORM_NODE,
        payload: {
            entities: {
                [ENTITIES_KEY_NODE]:
                    dropParentNodeId !== previousParentUpdated.id
                        ? // Prevents weird behaviour when dropping out of bounds.
                          {}
                        : {
                              [previousParentUpdated.id]: previousParentUpdated,
                              [newParentUpdated.id]: newParentUpdated,
                              [newDragNode.id]: newDragNode,
                          },
            },
        },
    };
};

export const endDragFormNode = () => (dispatch, getState) =>
    tryWrap(() => {
        const state = getState();
        const formId = getFormId(state);
        const dragNodeId = getDragNodeId(state);
        const dragNode = getFormNode(state, dragNodeId);
        const dropParentNodeId = getDropParentNodeId(state);
        const dropNodePosition = getDropNodePosition(state);

        const previousParentId = dragNode.parent;
        const previousParent = getFormNode(state, previousParentId);

        const blockedNodes = getBlockedNodes(state, formId).map((node) => node.id);

        // Changes in an additional guest field need to be applied to all additional guest groups.
        if (isAdditionalGuestInfoNode(state, formId, dragNodeId)) {
            const additionalNodesToDrop = getAdditionalGuestInfoNodes(state, formId, dragNode.crossGroupId);
            additionalNodesToDrop.forEach((additionalNode) => {
                dispatch(
                    createEndDragFormNodeAction(
                        additionalNode.id,
                        additionalNode.parent,
                        getFormNode(state, additionalNode.parent),
                        dropNodePosition,
                        additionalNode,
                        blockedNodes,
                    ),
                );
            });
        } else {
            dispatch(
                createEndDragFormNodeAction(
                    dragNodeId,
                    dropParentNodeId,
                    previousParent,
                    dropNodePosition,
                    dragNode,
                    blockedNodes,
                ),
            );
        }
    })();
